import React, { useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Avatar from "@mui/material/Avatar";
import Skeleton from "@mui/material/Skeleton";
import useClickOutside from "../../../../hooks/useClickOutside";
import { ReactComponent as Dropdown } from "../../../../assets/images/dropdown-arrow.svg";
import {
  CheckboxCheckedIcon,
  CheckboxUncheckedIcon,
  FilterCheckbox,
  FilterChipCloseIcon,
  PeopleSearchIcon,
  searchFieldSx,
} from "../Customers/customersFilterUiShared";
import { colorCodesForAvatar } from "../../../../constants/index";
import { chracterPosition } from "../../../../utils/index";

const triggerSx = {
  width: "100%",
  borderRadius: "8px",
  border: "1px solid #D5DCE1",
  background: "#FFF",
  boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
  height: 40,
  px: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer",
  boxSizing: "border-box",
};

const panelSx = {
  position: "absolute",
  bottom: "calc(100% + 8px)",
  left: 0,
  right: 0,
  zIndex: 1000,
  border: "1px solid #E5E7EB",
  borderRadius: "8px",
  p: 1,
  backgroundColor: "#FFF",
  boxShadow: "0 4px 12px rgba(6, 21, 34, 0.08)",
  display: "flex",
  flexDirection: "column",
  maxHeight: 224,
  boxSizing: "border-box",
};

function AgentChip({ label, onRemove }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.5,
        borderRadius: "20px",
        border: "1px solid #C5CED6",
        background: "#F9FAFA",
        maxWidth: "100%",
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          color: "#405261",
          fontFamily: "Inter",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Box
        component="button"
        type="button"
        onClick={onRemove}
        sx={{ border: "none", background: "transparent", p: 0, display: "flex", cursor: "pointer" }}
      >
        <FilterChipCloseIcon style={{ width: 16, height: 16, display: "block" }} />
      </Box>
    </Box>
  );
}

export default function TeamCreateAgentSelect({
  agentOptions = [],
  loading = false,
  selectedIds = [],
  onChange,
}) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);

  useClickOutside(wrapperRef, () => {
    setOpen(false);
    setSearchTerm("");
  });

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return agentOptions;
    return agentOptions.filter(
      (a) =>
        (a.name || "").toLowerCase().includes(q) ||
        (a.email || "").toLowerCase().includes(q)
    );
  }, [agentOptions, searchTerm]);

  const toggle = (id) => {
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((v) => v !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const removeId = (id) => {
    onChange(selectedIds.filter((v) => v !== id));
  };

  const triggerLabel =
    selectedIds.length === 0
      ? "Select agents"
      : `${selectedIds.length} agent${selectedIds.length === 1 ? "" : "s"} selected`;

  const agentLabel = (id) => agentOptions.find((a) => a.id === id)?.name || id;

  return (
    <Box>
      <Box ref={wrapperRef} sx={{ position: "relative", width: "100%" }}>
        {loading ? (
          <Skeleton variant="rounded" width="100%" height={40} sx={{ borderRadius: "8px" }} />
        ) : (
          <>
            <Box sx={triggerSx} onClick={() => setOpen((o) => !o)}>
              <Typography
                sx={{
                  color: selectedIds.length ? "#061522" : "#C5CED6",
                  fontSize: 14,
                  fontFamily: "Inter",
                }}
              >
                {triggerLabel}
              </Typography>
              <Dropdown
                style={{
                  width: 16,
                  height: 16,
                  transform: open ? "rotate(0deg)" : "rotate(180deg)",
                  transition: "transform 0.2s",
                  flexShrink: 0,
                }}
              />
            </Box>
            {open && (
              <Box sx={panelSx}>
                <TextField
                  fullWidth
                  autoFocus
                  variant="outlined"
                  placeholder="Search agents"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PeopleSearchIcon style={{ width: 16, height: 16, display: "block" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={searchFieldSx}
                />
                <Box
                  sx={{
                    mt: 1,
                    overflowY: "auto",
                    flex: 1,
                    minHeight: 0,
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(0,0,0,0.2) transparent",
                  }}
                >
                  {filtered.length === 0 ? (
                    <Typography sx={{ px: 1.5, py: 2, fontSize: 14, color: "#405261" }}>
                      No agents found
                    </Typography>
                  ) : (
                    filtered.map((agent) => {
                      const checked = selectedSet.has(agent.id);
                      const letter = (agent.name || "?").substring(0, 1).toUpperCase();
                      return (
                        <Box
                          key={agent.id}
                          onClick={() => toggle(agent.id)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1,
                            py: 1,
                            borderRadius: "8px",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#F9FAFA" },
                          }}
                        >
                          <FilterCheckbox
                            checked={checked}
                            icon={<CheckboxUncheckedIcon />}
                            checkedIcon={<CheckboxCheckedIcon />}
                            disableRipple
                            onChange={() => toggle(agent.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Avatar
                            src={agent.avatar || undefined}
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: 12,
                              flexShrink: 0,
                              bgcolor:
                                colorCodesForAvatar[chracterPosition(letter)] || "#405261",
                            }}
                          >
                            {letter}
                          </Avatar>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                color: "#061522",
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {agent.name}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: "#405261" }}>
                              {agent.role || "Agent"}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
      {selectedIds.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
          {selectedIds.map((id) => (
            <AgentChip key={id} label={agentLabel(id)} onRemove={() => removeId(id)} />
          ))}
        </Box>
      )}
    </Box>
  );
}
